<script>
import isFunction from 'lodash/isFunction';
import CompanyModel from '@models/Company';
import ContactModel from '@models/Contact';
import VipModel from '@models/Vip';
import JobModel from '@models/Job';
import JobStatusModel from '@models/JobStatus';
import UniversalModel from '@models/Universal';
import AdminModel from '@models/Admin';
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import utility from '@mixins/utility';
import ContactMultiselect from '@components/ContactMultiselect';
import EntityMultiselect from '@components/EntityMultiselect';
import AdminMultiselect from '@/src/components/AdminMultiselect';
import FileWrapper from '@components/FileWrapper';
import FileDownloadLink from '@components/FileDownloadLink';
import _findIndex from 'lodash/findIndex';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _merge from 'lodash/merge';
import _keyBy from 'lodash/keyBy';
import cloneDeep from 'lodash/cloneDeep';
import { mapState } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import datePickerUtility from '@mixins/datePickerUtility';
import ContactForm from '@views/contacts/form';
import CompanyForm from '@views/companies/form';
import ReportModel from '@models/Report';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ScheduledServiceTimes from '@views/jobs/scheduledServiceTimes/scheduled-service-times';

export default {
  components: {
    BlockingContainer,
    ContactMultiselect,
    EntityMultiselect,
    AdminMultiselect,
    FileWrapper,
    FileDownloadLink,
    flatPickr,
    ContactForm,
    CompanyForm,
    FontAwesomeIcon,
    ScheduledServiceTimes,
  },
  mixins: [editFormChange, datePickerUtility, utility],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    jobId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      reportModel: ReportModel,
      companyModel: CompanyModel,
      vipModel: VipModel,
      contactModel: ContactModel,
      adminModel: AdminModel,
      reportMultiselectSelect: [],
      reportMultiselectInclude: ['vip', 'company', 'user'],
      reportMultiselectWhere: [
        {
          name: 'search',
        },
      ],
      reportMultiselectURLParams: {
        all_drafts: true,
      },
      showContactModal: false,
      showCompanyModal: false,
      showPriceListModal: false,
      showVipModal: false,
      job_status_invoiced_id: 4,
      notCollapsed: {
        scheduled_service_times: false,
      },
      uniqueKey: Math.random().toString(36).substr(2, 9),
      scheduledServiceTimesLocal: [],
      job: {
        vip_id: null,
        job_status_id: null,
        contact_not_in_company: false,
        company_id: null,
        contact_id: null,
        organizer_id: null,
        remark: null,
        payment_link: 0,
        attachments: [],
        new_attachments: [],
        invoice_number: [],
        additional_costs_report_id: null,
        additional_costs_report: null,
        additional_cost_report_ids: [],
        additional_cost_reports: [],
      },
      jobStatuses: [],
      attachmentTableFields: [
        {
          key: 'name',
          label: '',
          sortable: false,
        },
      ],
      vip: {
        entity: 'Vip',
        name: '',
      },
      additionalCostsTableFields: [
        { key: 'report', label: this.$t('table_labels.report'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      invoice_number: null,
    };
  },
  computed: {
    ...mapState({
      dateOnlyConfig: (state) => state.common.dateOnlyConfig,
      currentUser: (state) => state.auth.user,
    }),
    contactNotInCompanyDisabled() {
      return this.job.company_id == null;
    },
    whereCompanyId() {
      return this.job.contact_not_in_company ? null : this.job.company_id;
    },
    sstKey() {
      return this.uniqueKey;
    },
  },
  mounted() {
    this.loadData();
    if ('create' in this.$route.query) {
      this.notCollapsed.scheduled_service_times = true;
    }
  },
  methods: {
    async loadData() {
      this.$wait.start('job statuses loading');
      await JobStatusModel.limit(10)
        .get()
        .then((response) => {
          this.jobStatuses = _map(response, (status) => {
            return { id: status.id, name: status.name };
          });
          this.$wait.end('job statuses loading');
        })
        .catch(() => {
          this.$wait.end('job statuses loading');
        });
      if (this.loadedId) {
        this.$wait.start('job loading');
        JobModel.find(this.loadedId).then((response) => {
          this.job = cloneDeep(response.job);
          this.scheduledServiceTimesLocal = this.job.scheduled_service_times || [];
          // exclude from job so it's not picked up by the entity-changed deep watcher below
          delete this.job.scheduled_service_times;
          this.jobStatuses = _merge(_keyBy(this.jobStatuses, 'id'), _keyBy([this.job.status], 'id'));
          this.$wait.end('job loading');
          this.$nextTick(() => {
            this.initChangeWatcher(['job'], this.validate);
          });
        });
      } else {
        // set current date as default start date
        this.job.job_status_id = 2; // set order as default
        // set admin as default organizer
        this.job.organizer = this.currentUser;
        this.job.organizer_id = this.currentUser.id;
      }
    },
    removeAttachment(item) {
      this.$wait.start('attachment-remove');
      this.$http.delete(`files/${item.id}`).then((response) => {
        const index = _findIndex(this.job.attachments, item);
        this.$delete(this.job.attachments, index);
        this.$wait.end('attachment-remove');
      });
    },
    resetFields() {
      this.job.vip_id = null;
      this.job.job_status_id = null;
      this.job.company_id = null;
      this.job.contact_id = null;
      this.job.organizer_id = null;
      this.job.new_attachments = [];
      this.job.remark = null;
      this.job.payment_link = 0;
      this.job.start_date = null;
      this.job.invoice_number = [];
      this.job.additional_costs_report_id = null;
      this.job.additional_costs_report = null;
      this.job.additional_cost_reports = [];
    },
    validate(callback) {
      this.$validator.validateAll('job-form').then((result) => {
        if (result) {
          this.$wait.start('job loading');
          this.submit(callback);
        }
      });
    },
    addNewInvoiceNumber() {
      if(this.invoice_number != null && this.invoice_number !== '' && !this.job.invoice_number.includes(this.invoice_number)) {
        this.job.invoice_number.push(this.invoice_number);
        this.invoice_number = null;
      }
    },
    addScheduledServiceTime(item) {
      this.scheduledServiceTimesLocal.push(item);
      // refresh component
      this.uniqueKey = Math.random().toString(36).substr(2, 9);
    },
    addScheduledServiceTimes(items) {
      items.forEach((sst) => {
        this.scheduledServiceTimesLocal.push(sst);
      });
      // refresh component
      this.uniqueKey = Math.random().toString(36).substr(2, 9);
    },
    updateScheduledServiceTime(item, key) {
      this.$set(this.scheduledServiceTimesLocal, key, item);
    },
    deleteScheduledServiceTime(id, key) {
      this.scheduledServiceTimesLocal.splice(key, 1);
    },
    deletedMultipleScheduledServiceTimes(keys) {
      // maintain reactivity this way
      keys.forEach((index, key) => {
        this.scheduledServiceTimesLocal.splice(index - key, 1);
      });
    },
    updatedMultipleScheduledServiceTimes(scheduledServiceTimes, keys) {
      keys.forEach((key, index) => {
        // bug fix for table
        scheduledServiceTimes[index]._showDetails = false;
        this.$set(this.scheduledServiceTimesLocal, key, scheduledServiceTimes[index]);
      });
    },
    removeInvoiceNumber(index) {
      this.job.invoice_number.splice(index, 1);
    },
    async deleteJob() {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.job') }))) {
        this.$wait.start('job processing');
        const id = this.loadedId;
        const job = new JobModel({ id });
        await job.delete();
        this.$wait.end('job processing');
        window.close();
      }
    },
    submit(callback) {
      let data = {};

      if (this.$route.params.id && this.jobId == null) {
        data.id = this.$route.params.id;
      }

      if (this.jobId) {
        data.id = this.jobId;
        callback = this.resetFields;
      }

      // set file properly
      if (this.job.new_attachments.length > 0) {
        data.new_attachments = this.job.new_attachments[0];
      }

      data.company_id = this.job.company_id;
      data.vip_id = this.job.vip_id;
      data.contact_id = this.job.contact_id;
      data.job_status_id = this.job.job_status_id;
      data.organizer_id = this.job.organizer_id;
      data.start_date = this.job.start_date;
      data.remark = this.job.remark;
      data.payment_link = this.job.payment_link;
      data.invoice_number = this.job.invoice_number;
      data.additional_cost_reports = _map(this.job.additional_cost_reports, 'id');

      new JobModel(data)
        .save()
        .then((response) => {
          this.$emit('submitted', response.job);
          this.job = cloneDeep(response.job);
          this.job.new_attachments = [];
          this.$wait.end('job loading');
          if (!callback) {
            this.$router.push({
              name: 'jobs.index',
            });
          } else if (isFunction(callback)) {
            callback();
          }
        })
        .catch(() => this.$wait.end('job loading'));
    },
    createVIP(event) {
      event.preventDefault();
      if (this.vip.name !== '') {
        this.$wait.start('global loading');
        new UniversalModel(this.vip)
          .save()
          .then((response) => {
            this.$wait.end('global loading');
            this.vipCreated(response.entity);
          })
          .catch(() => this.$wait.end('global loading'));
      }
    },
    contactCreated(contact) {
      this.job.contact_id = contact.id;
      this.job.contact = contact;
      this.showContactModal = false;
    },
    companyCreated(company) {
      this.job.company_id = company.id;
      this.job.company = company;
      this.showCompanyModal = false;
    },
    vipCreated(vip) {
      this.job.vip = vip;
      this.job.vip_id = vip.id;
      // reset vip
      this.vip.name = '';
      this.showVipModal = false;
    },
    addAdditionalCostReport(report) {
      if (report) {
        this.job.additional_cost_reports.push(report);
      }
      // reset
      this.job.additional_costs_report_id = null;
      this.job.additional_costs_report = null;
    },
    removeAdditionalCostReport(report) {
      this.$set(
        this.job,
        'additional_cost_reports',
        _filter(this.job.additional_cost_reports, function (r) {
          return r.id !== report.id;
        })
      );
    },
    exportPdf() {
      const id = this.$route.params.id;
      this.$wait.start(`downloading-job-${id}`);
      this.$http
        .get(`admin/jobs/${id}/export`, { responseType: 'blob' })
        .then((response) => {
          this.$wait.end(`downloading-job-${id}`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `Job #${id}`;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading-job-${id}`));
    },
  },
};
</script>

<template>
  <div>
    <b-card border-variant="dark" :header="$t('titles.job_details')" header-tag="header" header-bg-variant="dark" header-text-variant="white" class="mb-3">
      <BlockingContainer v-if="$wait.is('job loading')"></BlockingContainer>
      <b-form data-vv-scope="job-form" @submit.prevent="validate">
        <b-form-group>
          <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.start_date') }}:
            <span class="status-star text-danger">*</span>
          </template>
          <div :class="{ 'is-invalid': errors.has('start_date', 'job-form') }">
            <flat-pickr
              v-model="job.start_date"
              v-validate="'required'"
              data-vv-name="start_date"
              :disabled="!isJobEditable(job)"
              :data-vv-as="$t('fields.start_date')"
              aria-describedby="input-feedback-start_date"
              :config="dateOnlyConfig"
              class="form-control"
              :class="{ 'is-invalid': errors.has('start_date', 'job-form') }"
              :placeholder="$t('placeholders.start_date')"
              @on-close="closeCalendar('job.start_date', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)"
            >
            </flat-pickr>
            <b-form-invalid-feedback id="input-feedback-start_date">
              <template v-show="errors.has('start_date', 'job-form')">{{ errors.first('start_date', 'job-form') }} </template>
            </b-form-invalid-feedback>
          </div>
        </b-form-group>        
        <b-row>
          <b-col sm="12">
            <b-form-group label-class="col-form-legend">
              <template v-slot:label>
                {{ $t('fields.vip') }}:
                <span class="status-star text-danger">*</span>
              </template>
              <b-input-group>
                <div class="multiselect-container form-control p-0 border-0">
                  <EntityMultiselect
                    v-model="job.vip_id"
                    v-validate="'required'"
                    data-vv-name="vip_id"
                    :disabled="!isJobEditable(job)"
                    :data-vv-as="$t('fields.vip')"
                    :initial-value="job.vip"
                    :state="errors.has('vip_id', 'job-form')"
                    :placeholder="$t('placeholders.enter_at_least_three_letters')"
                    :model="vipModel"
                  >
                  </EntityMultiselect>
                </div>
                <b-input-group-append>
                  <b-btn variant="secondary" class="text-white" :disabled="!isJobEditable(job)" @click="showVipModal = true">
                    <FontAwesomeIcon :icon="['fas', 'plus']" />
                  </b-btn>
                </b-input-group-append>
              </b-input-group>
              <div v-show="errors.has('vip_id', 'job-form')" class="invalid-feedback d-block m-0 mt-1">
                {{ errors.first('vip_id', 'job-form') }}
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col sm="12">
            <b-form-group>
              <template v-slot:label>
                {{ $t('fields.company') }}:
                <div class="float-right"> </div>
              </template>
              <b-input-group>
                <div class="multiselect-container form-control p-0 border-0">
                  <EntityMultiselect
                    v-model="job.company_id"
                    v-validate="''"
                    data-vv-name="company_id"
                    :disabled="!isJobEditable(job)"
                    :data-vv-as="$t('fields.company')"
                    :initial-value="job.company"
                    :state="errors.has('company_id')"
                    :placeholder="$t('placeholders.enter_at_least_three_letters')"
                    :model="companyModel"
                  >
                  </EntityMultiselect>
                </div>
                <b-input-group-append>
                  <b-btn variant="secondary" class="w-100 text-white" :disabled="!isJobEditable(job)" @click="showCompanyModal = true">
                    <FontAwesomeIcon :icon="['fas', 'plus']" />
                  </b-btn>
                </b-input-group-append>
              </b-input-group>
              <div v-show="errors.has('company_id')" class="invalid-feedback d-block m-0 mt-1">
                {{ errors.first('company_id') }}
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col sm="12">
            <b-form-group>
              <template v-slot:label>
                {{ $t('fields.contact') }}:&nbsp;
                <b-form-checkbox
                  v-model="job.contact_not_in_company"
                  name="check-button"
                  switch
                  :value="true"
                  :unchecked-value="false"
                  :disabled="contactNotInCompanyDisabled || !isJobEditable(job)"
                  class="d-inline"
                >
                  <span>{{ $t('fields.contact_not_in_company') }}</span>
                </b-form-checkbox>
              </template>
              <b-input-group>
                <div class="multiselect-container form-control p-0 border-0">
                  <ContactMultiselect
                    v-model="job.contact_id"
                    v-validate="''"
                    data-vv-name="contact_id"
                    :disabled="!isJobEditable(job)"
                    :data-vv-as="$t('fields.contact')"
                    :initial-value="job.contact"
                    :state="errors.has('contact_id')"
                    :placeholder="$t('placeholders.enter_at_least_three_letters')"
                    :company="whereCompanyId"
                    sort="first_name"
                  >
                  </ContactMultiselect>
                </div>
                <b-input-group-append>
                  <b-btn class="w-100 text-white" variant="secondary" :disabled="!isJobEditable(job)" @click="showContactModal = true">
                    <FontAwesomeIcon :icon="['fas', 'plus']" />
                  </b-btn>
                </b-input-group-append>
              </b-input-group>
              <div v-show="errors.has('contact_id')" class="invalid-feedback d-block m-0 mt-1">
                {{ errors.first('contact_id') }}
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group :label="`${$t('fields.organizer')}:`">
          <AdminMultiselect
            v-model="job.organizer_id"
            v-validate="''"
            data-vv-name="organizer"
            :disabled="!isJobEditable(job)"
            :data-vv-as="$t('fields.organizer')"
            :initial-value="job.organizer"
            :state="errors.has('organizer_id')"
            :placeholder="$t('placeholders.organizer')"
            :model="adminModel"
          >
          </AdminMultiselect>
          <b-form-invalid-feedback id="input-feedback-organizer_id">
            <template v-show="errors.has('organizer_id')">{{ errors.first('organizer_id') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-button type="submit" :disabled="errors.any() || !isJobEditable(job)" :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')">
          {{ $t('actions.submit') }}
        </b-button>
        <div class="float-right">
          <b-button v-if="loadedId" variant="outline-danger" class="ml-2" @click="deleteJob">
            {{ $t('actions.delete') }}
          </b-button>
        </div>
      </b-form>
      <b-modal v-if="showContactModal" v-model="showContactModal" size="lg" hide-footer :title="$t('titles.create_contact')" static @hidden="$emit('hidden')">
        <ContactForm :company-id="job.company_id" :is-modal="true" @submitted="contactCreated" />
      </b-modal>
      <b-modal v-if="showCompanyModal" v-model="showCompanyModal" size="lg" hide-footer :title="$t('titles.create_company')" static @hidden="$emit('hidden')">
        <CompanyForm :is-modal="true" @submitted="companyCreated" />
      </b-modal>
      <b-modal v-if="showVipModal" v-model="showVipModal" size="lg" hide-footer :title="$t('titles.create_vip')" static @hidden="$emit('hidden')">
        <b-form @submit="createVIP">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.name') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <b-form-input
              id="name"
              v-model="vip.name"
              v-validate="'required|max:255'"
              data-vv-value-path="localValue"
              data-vv-name="name"
              :data-vv-as="$t('fields.name')"
              type="text"
              :state="errors.has('name') ? false : null"
              aria-describedby="input-feedback-name"
              :placeholder="$t('placeholders.name')"
            >
            </b-form-input>
            <b-form-invalid-feedback id="input-feedback-name">
              <template v-show="errors.has('name')">{{ errors.first('name') }}</template>
            </b-form-invalid-feedback>
            <b-button :disabled="vip.name === ''" type="submit" variant="outline-dark" class="mt-2">
              {{ $t('actions.submit') }}
            </b-button>
          </b-form-group>
        </b-form>
      </b-modal>
      <b-modal
        ref="pricelist-modal"
        v-model="showPriceListModal"
        resizable
        hide-footer
        scrollable
        centered
        dialog-class="custom-modal-dialog"
        :title="$t('titles.price_list')"
      >
        <b-img :src="require('@assets/images/KKS_Pricelist.png')" fluid alt="Price List" />
      </b-modal>
    </b-card>
    <b-card v-if="job.id" border-variant="dark" header-tag="header" header-bg-variant="dark" header-text-variant="white" class="mb-3">
      <template v-slot:header>
        <div>
          {{ $t('titles.service_times') }}<template v-if="scheduledServiceTimesLocal.length"> ({{ scheduledServiceTimesLocal.length }})</template>
          <span class="fa-pull-right cursor-pointer" @click.stop="notCollapsed.scheduled_service_times = !notCollapsed.scheduled_service_times">
            <span>{{ $t('titles.new_service_time') }}</span
            >&nbsp;
            <FontAwesomeIcon :icon="['fas', notCollapsed.scheduled_service_times ? 'chevron-down' : 'chevron-right']" />
          </span>
        </div>
      </template>
      <ScheduledServiceTimes
        :key="sstKey"
        ref="scheduledServiceTimes"
        :job="job"
        :scheduled-service-times="scheduledServiceTimesLocal"
        :collapsed.sync="notCollapsed.scheduled_service_times"
        @submitted="addScheduledServiceTime"
        @duplicated="addScheduledServiceTimes"
        @updated="updateScheduledServiceTime"
        @deleted="deleteScheduledServiceTime"
        @deleted-multiple="deletedMultipleScheduledServiceTimes"
        @updated-multiple="updatedMultipleScheduledServiceTimes"
      />
    </b-card>
    <b-card border-variant="dark" :header="$t('titles.processing')" header-tag="header" header-bg-variant="dark" header-text-variant="white" class="mb-3">
      <BlockingContainer v-if="$wait.is('job loading')"></BlockingContainer>
      <b-form data-vv-scope="job-form" @submit.prevent="validate">
        <b-form-group>
          <template v-slot:label class="col-form-legend col-12">
            {{ $t('fields.status') }}:
            <span class="status-star text-danger">*</span>
          </template>
          <b-form-select
            v-model="job.job_status_id"
            v-validate="'required'"
            data-vv-value-path="localValue"
            data-vv-name="job_status_id"
            :disabled="!isJobEditable(job)"
            :data-vv-as="$t('fields.status')"
            :state="errors.has('job_status_id') ? false : null"
            aria-describedby="input-feedback-job_status_id"
          >
            <option :value="null">{{ $t('placeholders.status') }}</option>
            <option v-for="status in jobStatuses" :key="`jobTypeSelect${status.id}`" :value="status.id">
              {{ status.name }}
            </option>
          </b-form-select>
          <b-form-invalid-feedback id="input-feedback-job_status_id">
            <template v-show="errors.has('job_status_id')">{{ errors.first('job_status_id') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group>
          <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.remark') }}:
            <FontAwesomeIcon :icon="['fas', 'file']" class="text-secondary cursor-pointer" @click="showPriceListModal = true"/>
          </template>
          <b-form-textarea
            id="remark"
            v-model="job.remark"
            v-validate="'max:65000'"
            data-vv-value-path="localValue"
            data-vv-name="remark"
            :disabled="!isJobEditable(job)"
            :rows="3"
            :state="errors.has('remark', 'job-form') ? false : null"
            aria-describedby="input-feedback-remark"
          >
          </b-form-textarea>
        </b-form-group>
        <b-form-group>
          <b-form-checkbox
            v-model="job.payment_link"
            :disabled="!isJobEditable(job)"
            :unchecked-value="0"
            :value="1"
            switch
          >
            {{ $t('fields.payment_link') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-row>
          <b-col>
            <b-form-group>
              <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.attachments') }}: </template>
              <FileWrapper
                v-model="job.new_attachments"
                v-validate="''"
                :disabled="!isJobEditable(job)"
                :data-vv-as="$t('fields.attachments')"
                :state="errors.has('new_attachments', 'job-form') ? false : null"
                :placeholder="$t('placeholders.attachments')"
                accept=".docx, .doc, .pdf, .jpg, .png, .jpeg, .xls, .xlsx"
                aria-describedby="input-feedback-new_attachments"
                name="new_attachments"
              >
              </FileWrapper>
            </b-form-group>
          </b-col> </b-form-row
        >
        <b-form-row v-if="loadedId && job.attachments.length > 0">
          <b-col>
            <b-table stacked="lg" striped hover :items="job.attachments" :fields="attachmentTableFields" small thead-class="d-none">
              <template v-slot:cell(name)="row">
                <FileDownloadLink :file="row.item" @remove="removeAttachment" />
              </template>
            </b-table>
          </b-col>
        </b-form-row>
        <b-form-group>
          <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.additional_costs') }}: </template>
          <b-input-group class="border-muted">
            <div class="form-control border-muted p-0">
              <EntityMultiselect
                key="report-input"
                v-model="job.additional_costs_report_id"
                v-validate="''"
                data-vv-name="additional_costs_report_id"
                :disabled="!isJobEditable(job)"
                :data-vv-as="$t('fields.report')"
                :initial-value="job.additional_costs_report"
                :state="errors.has('additional_costs_report_id', 'job-form')"
                :placeholder="$t('placeholders.report')"
                sort="id"
                :model="reportModel"
                :select="reportMultiselectSelect"
                :where="reportMultiselectWhere"
                :include="reportMultiselectInclude"
                :params="reportMultiselectURLParams"
                :clear-on-change="true"
                @change-val="addAdditionalCostReport"
              >
              </EntityMultiselect>
            </div>
          </b-input-group>
          <b-table
            v-if="job.additional_cost_reports.length > 0"
            ref="additionalCostReportsTable"
            small
            striped
            :items="job.additional_cost_reports"
            stacked="md"
            :fields="additionalCostsTableFields"
            :no-sort-reset="true"
          >
            <template v-slot:cell(actions)="data">
              <b-btn class="mr-2" size="sm" variant="outline-danger" @click.stop="removeAdditionalCostReport(data.item)">
                <FontAwesomeIcon :icon="['fas', 'times-circle']" />
              </b-btn>
            </template>
            <template v-slot:cell(report)="data">
              <router-link :to="{ name: 'reports.edit', params: { id: data.item.id } }">#{{ data.item.id }} - {{ data.item.user.name }}</router-link>
            </template>
          </b-table>
        </b-form-group>
        <b-form-group v-if="!$can.not('doAccounting')">
          <template v-slot:label class="d-flex">
            <span>{{ $t('fields.invoice_number') }}:</span>
          </template>
          <b-input-group class="mb-2">
            <b-form-input
              v-model="invoice_number"
              v-validate="'max:255'"
              data-vv-value-path="localValue"
              data-vv-name="invoice_number"
              :disabled="!isJobEditable(job)"
              :data-vv-as="$t('fields.invoice_number')"
              type="text"
              :state="errors.has('invoice_number') ? false : null"
              aria-describedby="input-feedback-first_name"
              :placeholder="$t('placeholders.invoice_number')"
            >
            </b-form-input>
            <b-input-group-append>
              <b-btn variant="secondary" size="sm" @click="addNewInvoiceNumber(index)">{{ $t('actions.add') }}</b-btn>
            </b-input-group-append>
          </b-input-group>
          <b-input-group v-for="(invoiceNumber, index) in job.invoice_number" :id="`'in-'${index}`" :key="`'in-'${index}`" class="mb-2">
            <b-form-input
              v-model="job.invoice_number[index]"
              v-validate="'max:255'"
              data-vv-value-path="localValue"
              data-vv-name="invoice_number"
              :disabled="!isJobEditable(job)"
              :data-vv-as="$t('fields.invoice_number')"
              type="text"
              :state="errors.has('invoice_number') ? false : null"
              aria-describedby="input-feedback-first_name"
              :placeholder="$t('placeholders.invoice_number')"
            >
            </b-form-input>
            <b-input-group-append>
              <b-btn variant="danger" size="sm" @click="removeInvoiceNumber(index)">{{ $t('actions.remove') }}</b-btn>
            </b-input-group-append>
          </b-input-group>
          <b-form-invalid-feedback id="input-feedback-invoice_number">
            <template v-show="errors.has('invoice_number')">{{ errors.first('invoice_number') }}</template>
          </b-form-invalid-feedback>
        </b-form-group><br />
        <b-button type="submit" :disabled="errors.any() || !isJobEditable(job)" :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')">
          {{ $t('actions.submit') }}
        </b-button>
        <div class="float-right">
          <b-button type="button"  variant="outline-dark" @click="exportPdf">
            {{ $t('actions.download') }}
          </b-button>
        </div>
      </b-form>
      <b-modal v-if="showContactModal" v-model="showContactModal" size="lg" hide-footer :title="$t('titles.create_contact')" static @hidden="$emit('hidden')">
        <ContactForm :company-id="job.company_id" :is-modal="true" @submitted="contactCreated" />
      </b-modal>
      <b-modal v-if="showCompanyModal" v-model="showCompanyModal" size="lg" hide-footer :title="$t('titles.create_company')" static @hidden="$emit('hidden')">
        <CompanyForm :is-modal="true" @submitted="companyCreated" />
      </b-modal>
      <b-modal v-if="showVipModal" v-model="showVipModal" size="lg" hide-footer :title="$t('titles.create_vip')" static @hidden="$emit('hidden')">
        <b-form @submit="createVIP">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.name') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <b-form-input
              id="name"
              v-model="vip.name"
              v-validate="'required|max:255'"
              data-vv-value-path="localValue"
              data-vv-name="name"
              :data-vv-as="$t('fields.name')"
              type="text"
              :state="errors.has('name') ? false : null"
              aria-describedby="input-feedback-name"
              :placeholder="$t('placeholders.name')"
            >
            </b-form-input>
            <b-form-invalid-feedback id="input-feedback-name">
              <template v-show="errors.has('name')">{{ errors.first('name') }}</template>
            </b-form-invalid-feedback>
            <b-button :disabled="vip.name === ''" type="submit" variant="outline-dark" class="mt-2">
              {{ $t('actions.submit') }}
            </b-button>
          </b-form-group>
        </b-form>
      </b-modal>
      <b-modal
        ref="pricelist-modal"
        v-model="showPriceListModal"
        resizable
        hide-footer
        scrollable
        centered
        dialog-class="custom-modal-dialog"
        :title="$t('titles.price_list')"
      >
        <b-img :src="require('@assets/images/KKS_Pricelist.png')" fluid alt="Price List" />
      </b-modal>
    </b-card>
  </div>  
</template>

<style>
.custom-modal-dialog {
  width: auto;
  max-width: 100%;
}

.custom-modal-dialog .modal-content{
  padding: 0;
  width: auto;
}

.custom-modal-dialog .modal-body {
    padding: 0;
}
</style>
<style lang="scss" module>
@import '@design';
.avatarImg {
  height: 160px;

  &:hover {
    cursor: pointer;
    border: 1px solid map_get($theme-colors, 'dark');
  }
}

.clearButtonContainer {
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 0 !important;
}
</style>

<style lang="scss" scoped>
.input-group-append .btn {
  min-width: 80px;
}
</style>
